import { useEffect, useMemo, useState } from "react";
import { tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import {
  Globe, FileSpreadsheet, Webhook, Database, Boxes, Plug, Cable,
} from "lucide-react";

export const CONNECTOR_ICON = {
  REST_API: Globe, FILE_UPLOAD: FileSpreadsheet, WEBHOOK: Webhook,
  DATABASE: Database, ERP_SAP: Boxes, ERP_ORACLE: Boxes, ERP_DYNAMICS: Boxes, ERP_TALLY: Boxes,
};
export const ConnectorIcon = ({ code, className }) => {
  const Icon = CONNECTOR_ICON[code] || Plug;
  return <Icon className={className} />;
};

export const STATUS_STYLE = {
  Connected: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  Draft: "text-muted-foreground bg-muted/40 border-border",
  Disabled: "text-muted-foreground bg-muted/40 border-border",
  Error: "text-red-600 bg-red-500/10 border-red-500/30",
};
export const RUN_STYLE = {
  completed: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  completed_with_errors: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  running: "text-sky-600 bg-sky-500/10 border-sky-500/30",
  failed: "text-red-600 bg-red-500/10 border-red-500/30",
};
export const ERROR_STYLE = {
  pending: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  resolved: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
  dead: "text-red-600 bg-red-500/10 border-red-500/30",
};

export function Badge({ label, styleMap, testid }) {
  const cls = (styleMap || {})[label] || "text-muted-foreground bg-muted/40 border-border";
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border capitalize ${cls}`} data-testid={testid}>
      {String(label || "").replace(/_/g, " ")}
    </span>
  );
}

export const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

export function useTenantScope() {
  const { user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  useEffect(() => {
    if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {});
  }, [isSuper]);
  const params = useMemo(() => (tenantId ? { tenantId } : {}), [tenantId]);
  return { isSuper, tenants, tenantId, setTenantId, params };
}

export function TenantPicker({ isSuper, tenants, tenantId, setTenantId }) {
  if (!isSuper) return null;
  return (
    <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
      className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
      data-testid="integration-tenant-select">
      <option value="">Select tenant…</option>
      {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
    </select>
  );
}

export function SelectTenantNotice() {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="select-tenant-notice">
      <Cable className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
      <h3 className="text-base font-semibold text-foreground">Select a tenant</h3>
      <p className="text-sm text-muted-foreground mt-1">Choose a tenant above to view its integrations.</p>
    </div>
  );
}

export function PageHeader({ icon: Icon, title, subtitle, children }) {
  return (
    <header className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {Icon && <Icon className="h-5 w-5" />}
        </span>
        <div>
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </header>
  );
}
